import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Search } from 'lucide-react';
import { chatService, PessoaDoChat } from '../../services/chatService';

interface NovaConversaDiretaProps {
  organizationId: string;
  meuAuthId: string | null;
  onEscolher: (pessoa: PessoaDoChat) => void;
  onCancelar: () => void;
}

// Mesma busca da lista de conversas: sem acento e sem caixa.
const normalizar = (texto: string): string =>
  texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

// A lista de pessoas muda pouco e o popup do chat desmonta a cada vez que
// fecha; sem cache, cada "nova conversa" refazia a busca.
const pessoasCache = new Map<string, PessoaDoChat[]>();

export const Avatar: React.FC<{ nome: string; url?: string | null; tamanho?: number }> = ({
  nome,
  url,
  tamanho = 20,
}) => {
  const [falhou, setFalhou] = useState(false);
  const estilo = { width: tamanho, height: tamanho };

  if (url && !falhou) {
    return (
      <img
        src={url}
        alt=""
        style={estilo}
        className="rounded-full object-cover flex-shrink-0"
        onError={() => setFalhou(true)}
      />
    );
  }

  return (
    <span
      style={{ ...estilo, fontSize: Math.max(9, Math.round(tamanho * 0.45)) }}
      className="rounded-full bg-gray-200 text-gray-600 font-semibold flex items-center justify-center flex-shrink-0"
      aria-hidden="true"
    >
      {(nome.trim()[0] || '?').toUpperCase()}
    </span>
  );
};

const NovaConversaDireta: React.FC<NovaConversaDiretaProps> = ({
  organizationId,
  meuAuthId,
  onEscolher,
  onCancelar,
}) => {
  const [pessoas, setPessoas] = useState<PessoaDoChat[] | null>(
    pessoasCache.get(organizationId) ?? null
  );
  const [erro, setErro] = useState<string | null>(null);
  const [termo, setTermo] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (pessoasCache.has(organizationId)) return;

    let cancelado = false;
    chatService
      .getPessoasDaOrganizacao(organizationId)
      .then(lista => {
        pessoasCache.set(organizationId, lista);
        if (!cancelado) setPessoas(lista);
      })
      .catch(() => {
        if (!cancelado) setErro('Não foi possível carregar as pessoas da organização.');
      });

    return () => {
      cancelado = true;
    };
  }, [organizationId]);

  const visiveis = useMemo(() => {
    const busca = normalizar(termo);
    return (pessoas || [])
      .filter(p => p.authId !== meuAuthId)
      .filter(
        p =>
          !busca ||
          normalizar(p.nome).includes(busca) ||
          (p.email ? normalizar(p.email).includes(busca) : false)
      );
  }, [pessoas, termo, meuAuthId]);

  return (
    <div className="chat-conversation-list">
      <div className="chat-conversation-list-header">
        <button
          type="button"
          onClick={onCancelar}
          className="p-1.5 rounded hover:bg-gray-200 text-gray-500 flex-shrink-0"
          title="Voltar para as conversas"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <input
            ref={inputRef}
            type="text"
            value={termo}
            onChange={e => setTermo(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') onCancelar();
              if (e.key === 'Enter' && visiveis.length === 1) onEscolher(visiveis[0]);
            }}
            placeholder="Conversar com..."
            className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-coral"
          />
        </div>
      </div>

      <div className="chat-conversation-list-body">
        <h4 className="chat-conversation-group-title">Nova conversa privada</h4>
        {erro ? (
          <p className="text-center text-xs text-nk-error py-6 px-3">{erro}</p>
        ) : pessoas === null ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-coral" />
          </div>
        ) : visiveis.length === 0 ? (
          <p className="text-center text-xs text-gray-500 py-6 px-3">
            {termo ? 'Ninguém encontrado' : 'Nenhuma outra pessoa na organização'}
          </p>
        ) : (
          <ul className="space-y-0.5">
            {visiveis.map(pessoa => (
              <li key={pessoa.authId}>
                <button
                  type="button"
                  onClick={() => onEscolher(pessoa)}
                  className="w-full text-left px-2 py-2 rounded-md flex items-center gap-2 hover:bg-gray-100 text-gray-700 transition-colors"
                >
                  <Avatar nome={pessoa.nome} url={pessoa.avatarUrl} tamanho={24} />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate text-sm font-medium">{pessoa.nome}</span>
                    {pessoa.email && pessoa.email !== pessoa.nome && (
                      <span className="block truncate text-xs text-gray-500">{pessoa.email}</span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default NovaConversaDireta;
